import React, { FC, useLayoutEffect } from 'react'

import {
  EnvelopeFullIcon,
  IconSizes,
  LightningBoltFullIcon
} from '@inno-ui/icons'
import { useTranslation } from 'react-i18next'
import type { GetCmResponsibleResponse } from 'shared/api/afterSalesApi'

import { useSetGlassConfig } from '../../GlassConfigContext'
import styles from './ContactPersonRightPanel.module.scss'

interface ContactPersonRightPanelProps {
  cmResponsible: NonNullable<GetCmResponsibleResponse['data']>
}

export const ContactPersonRightPanel: FC<ContactPersonRightPanelProps> = ({
  cmResponsible
}) => {
  const setGlassConfig = useSetGlassConfig()
  const { t } = useTranslation('translation', {
    keyPrefix:
      'desktop.full_page_onboarding.main_screen.steps.contact_person.right_panel'
  })

  const name =
    cmResponsible.name ??
    `${cmResponsible.first_name}${cmResponsible.last_name ? ` ${cmResponsible.last_name}` : ''}`
  const initials =
    `${cmResponsible.first_name.charAt(0)}${cmResponsible.last_name?.charAt(0) ?? ''}`.toUpperCase()

  useLayoutEffect(() => {
    setGlassConfig?.(null)
  }, [setGlassConfig])

  return (
    <section className={styles.panel} aria-label={name}>
      <div className={styles.personDetails}>
        {cmResponsible.avatar ? (
          <img
            className={styles.avatar}
            src={cmResponsible.avatar}
            alt={name}
            draggable={false}
          />
        ) : (
          <div className={styles.avatar} role='img' aria-label={name}>
            {initials}
          </div>
        )}
        <div className={styles.namePosition}>
          <p className={styles.name}>{name}</p>
          <p className={styles.position}>{t('position')}</p>
        </div>
      </div>

      <div className={styles.contactInfo}>
        <a
          className={styles.contactItem}
          href={`mailto:${cmResponsible.email}`}
        >
          <span className={styles.iconWrapper}>
            <EnvelopeFullIcon
              aria-hidden='true'
              color='var(--white)'
              size={IconSizes.Size18}
            />
          </span>
          <span className={styles.contactCopy}>
            <span className={styles.label}>{t('info.email.label')}</span>
            <span className={styles.value}>{cmResponsible.email}</span>
          </span>
        </a>

        <div className={styles.contactItem}>
          <span className={styles.iconWrapper}>
            <LightningBoltFullIcon
              aria-hidden='true'
              color='#FFCB54'
              size={IconSizes.Size18}
            />
          </span>
          <span className={styles.contactCopy}>
            <span className={styles.label}>{t('info.response.label')}</span>
            <span className={styles.value}>{t('info.response.value')}</span>
          </span>
        </div>
      </div>
    </section>
  )
}
